<template>
    <div class="leaderboard-page">
        <page-hero title="Leaderboard" :subtitle="seasonLabel">
            <b-select
                v-if="seasons.length"
                :value="selectedSeasonId"
                class="season-select"
                @input="selectedSeasonId = $event">
                <option v-for="season in seasons" :key="season.id" :value="season.id">
                    {{ season.name }}
                </option>
            </b-select>
        </page-hero>

        <div class="leaderboard-content">
            <div class="leaderboard-card box">
                <p v-if="loading" class="leaderboard-empty">Loading the leaderboard…</p>
                <p v-else-if="error" class="leaderboard-empty">{{ error }}</p>
                <p v-else-if="!entries.length" class="leaderboard-empty">
                    Nobody's played this season yet. Be the first.
                </p>

                <ul v-else class="leaderboard-list">
                    <li
                        v-for="entry in entries"
                        :key="entry.rank"
                        class="leaderboard-row"
                        :class="{ 'is-first': entry.rank === 1 }">
                        <span class="rank">{{ entry.rank }}</span>

                        <span class="avatar" :style="!entry.avatar ? avatarFallbackStyle(entry) : null">
                            <img v-if="entry.avatar" :src="entry.avatar" :alt="displayName(entry)" />
                            <template v-else>{{ initial(entry) }}</template>
                        </span>

                        <span class="identity">
                            <span class="name">{{ displayName(entry) }}</span>
                            <span v-if="titleFor(entry)" class="title-label">{{ titleFor(entry) }}</span>
                        </span>

                        <span class="most-played">
                            <b-tooltip
                                v-for="game in mostPlayedFor(entry)"
                                :key="game.id"
                                :label="`${game.name} — ${game.wins} wins`">
                                <span class="circle" :style="game.styles">{{ game.id }}</span>
                            </b-tooltip>
                        </span>

                        <span class="stat wins">
                            <b>{{ totalWins(entry.stats) }}</b>
                            <small>WINS</small>
                        </span>

                        <!-- Rank 1 gets its progress toward the next level
                             instead of a bare number — it's the one row with
                             room for it, and it's the row someone lands on
                             wondering "how close is #1 to leveling". -->
                        <span v-if="entry.rank === 1" class="stat level-progress">
                            <div class="level-bar">
                                <div class="level-bar-fill" :style="{ width: levelProgressPercent(entry) }"></div>
                            </div>
                            <small>LEVEL {{ entry.level }}</small>
                        </span>
                        <span v-else class="stat level">
                            <b>{{ entry.level }}</b>
                            <small>LEVEL</small>
                        </span>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import PageHero from '../components/Page/PageHero.vue'
import { XP_LEVELS } from '../assets/js/rewards.js'
import { colorsForGame } from '../globals/games.js'
import { mostPlayed, totalWins, levelProgress } from '../util/profile.js'

export default {
    name: 'Leaderboard',
    components: { PageHero },
    data() {
        return {
            entries: [],
            loading: true,
            error: null,
            selectedSeasonId: null,
        }
    },
    computed: {
        seasons() {
            return (this.$store.state.profile.season && this.$store.state.profile.season.seasons) || []
        },
        seasonLabel() {
            const season = this.seasons.find(s => s.id === this.selectedSeasonId)
            return season ? season.name : ''
        },
        gameNames() {
            return this.$store.state.profile.games.reduce((map, game) => {
                map[game.id] = game.name
                return map
            }, {})
        },
        achievementsById() {
            return this.$store.getters['profile/achievementsById']
        },
    },
    watch: {
        selectedSeasonId() {
            this.fetchLeaderboard()
        },
    },
    async created() {
        // The current season, plus every game/achievement name the rows
        // below need — none of this is leaderboard-specific, so it's the
        // same store data the profile page already populates.
        await this.$store.dispatch('profile/fetchSeason')
        await this.$store.dispatch('profile/fetchGames')
        this.$store.dispatch('profile/fetchAchievements')

        // Falls back to season 0 (not null) if fetchSeason failed — the
        // watcher below still fires and fetches that season, harmless, and
        // fetchLeaderboard() here covers the ordinary case without waiting
        // on the watcher's next tick.
        this.selectedSeasonId = (this.$store.state.profile.season && this.$store.state.profile.season.id) || 0
        await this.fetchLeaderboard()
    },
    methods: {
        async fetchLeaderboard() {
            if (this.selectedSeasonId === null) return
            this.loading = true
            this.error = null
            try {
                const res = await fetch(`/api/leaderboard?season=${this.selectedSeasonId}`)
                if (!res.ok) throw new Error()
                const json = await res.json()
                this.entries = json.entries || []
            } catch (err) {
                this.error = 'Could not load the leaderboard. Try again in a moment.'
            } finally {
                this.loading = false
            }
        },
        displayName(entry) {
            const id = String(entry.userID || '')
            return entry.username || `Player ${id.slice(-4) || '????'}`
        },
        initial(entry) {
            return this.displayName(entry).charAt(0).toUpperCase()
        },
        avatarFallbackStyle(entry) {
            // A stable colour per user rather than one flat grey for
            // everyone without a resolved avatar — cheap way to keep fifty
            // blank rows from all looking identical. The API always sends a
            // string id, but this renders from whatever the API sent, not
            // whatever it's supposed to send.
            const id = String(entry.userID || '')
            const hue = id.split('').reduce((sum, ch) => sum + ch.charCodeAt(0), 0) % 360
            return { backgroundColor: `hsl(${hue}, 55%, 55%)` }
        },
        titleFor(entry) {
            const achievement = entry.title && this.achievementsById[entry.title]
            return achievement ? achievement.name : null
        },
        mostPlayedFor(entry) {
            return mostPlayed(entry.stats, this.gameNames).map(game => {
                const colors = colorsForGame(game.id)
                return { ...game, styles: { backgroundColor: colors.background, color: colors.text } }
            })
        },
        totalWins(stats) {
            return totalWins(stats)
        },
        levelProgressPercent(entry) {
            return `${(levelProgress(entry.xp, entry.level, XP_LEVELS) * 100).toFixed(1)}%`
        },
    },
}
</script>

<style lang="scss" scoped>
.leaderboard-content {
    // The page gradient canvas (App.vue) sits absolutely positioned at
    // z-index: 0 for 700px down every page — plain in-flow content still
    // paints under it unless it opts into its own stacking context, same
    // fix Shop.vue uses for its content box.
    position: relative;
    z-index: 2;
    max-width: $widescreen;
    margin: 0 auto;
    padding: 0 20px 60px;
}

.season-select {
    margin-top: 0.5rem;
}

.leaderboard-card {
    border-radius: 16px;
    padding: 1rem;
}

.leaderboard-empty {
    text-align: center;
    padding: 3rem 1rem;
    opacity: 0.6;
}

.leaderboard-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.leaderboard-row {
    display: grid;
    grid-template-columns: 2.5rem 3rem 1fr auto auto auto;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 1rem;
    border-radius: 12px;
    background: #f4f4f6;

    &.is-first {
        background: linear-gradient(135deg, rgba(82, 217, 233, 0.12), rgba(0, 255, 171, 0.12));
        border: 1px solid rgba(82, 217, 233, 0.3);
    }

    @include mobile {
        grid-template-columns: 2rem 2.5rem 1fr auto;

        .most-played {
            display: none;
        }
    }
}

.rank {
    font-weight: bold;
    text-align: center;
    opacity: 0.7;
}

.avatar {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    flex-shrink: 0;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

.identity {
    display: flex;
    flex-direction: column;
    min-width: 0;

    .name {
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .title-label {
        font-size: 0.75rem;
        opacity: 0.6;
        text-transform: uppercase;
        letter-spacing: 0.03em;
    }
}

.most-played {
    display: flex;
    gap: 0.25rem;

    .circle {
        width: 1.75rem;
        height: 1.75rem;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.65rem;
        font-weight: bold;
    }
}

.stat {
    text-align: center;
    min-width: 4rem;

    b {
        display: block;
        font-size: 1.1rem;
    }

    small {
        opacity: 0.6;
        font-size: 0.7rem;
        letter-spacing: 0.03em;
    }
}

.level-progress {
    min-width: 8rem;

    .level-bar {
        height: 6px;
        border-radius: 3px;
        background: rgba(0, 0, 0, 0.1);
        overflow: hidden;
        margin-bottom: 0.25rem;
    }

    .level-bar-fill {
        height: 100%;
        background: $primary;
        border-radius: 3px;
    }
}
</style>
